<template>
	<div class="account-settings lok-scrollbar">
		<!-- TOP ACCOUNT SETTINGS -->
		<div
			v-show="!item.hideItem"
			v-for="(item, i) in accountItems"
			:key="i"
			class="account-settings__row"
			:class="item.type"
		>
			<div class="account-settings__row-left">
				{{ translate(`ui.${item.text}`) }}
			</div>
			<div class="account-settings__row-right">
				<div
					v-if="
						item.type !== AccountItemType.USERNAME ||
						(item.type === AccountItemType.USERNAME &&
							!editingInProgress &&
							userStore.userName)
					"
					class="account-settings__row-right-text"
					:id="item.type"
				>
					{{ item.value }}
				</div>
				<AppIconButton
					v-if="item.type !== AccountItemType.EMAIL"
					class="account-settings__row-button"
					:type="btnIcon(item.type)"
					@click="btnAction(item.type)"
				/>
			</div>
		</div>
		<div v-if="linkedSocialMediaType?.length > 0" class="link-account">
			<div class="link-account__title">
				{{ translate("ui.account_linked") }}
			</div>
			<div class="link-account--linked">
				<div
					class="link-account--linked--text"
					v-for="socialMediaType in linkedSocialMediaType"
					:key="socialMediaType"
				>
					{{ getSocialMediaTypeText(socialMediaType) }}
					<div class="icon-check" />
				</div>
			</div>
		</div>
		<!-- <div v-if="linkedSocialMediaType?.length < 2" class="link-account"> -->
		<div v-else class="link-account">
			<div class="link-account__title">
				{{ translate("ui.link_account") }}
			</div>
			<div class="link-account--row">
				<AppTextButton
					v-if="
						!linkedSocialMediaType?.includes(SocialMediaType.GOOGLE)
					"
					class="link-account__btn"
					:type="StatusTypes.DEFAULT"
					:text="translate('button.google.non_translatable')"
					@click="linkAccount(undefined, SocialMediaType.GOOGLE)"
				/>
				<AppTextButton
					v-if="
						!linkedSocialMediaType?.includes(SocialMediaType.APPLE)
					"
					class="link-account__btn"
					:type="StatusTypes.DEFAULT"
					:text="translate('button.apple_id.non_translatable')"
					@click="linkAccount(undefined, SocialMediaType.APPLE)"
				/>
			</div>
		</div>
		<HorizontalDecorDivider />
		<!-- SERVICE BUTTONS -->
		<div class="service-buttons">
			<div class="service-buttons__row">
				<AppTextButton
					class="service-buttons__btn"
					:type="StatusTypes.DEFAULT"
					:text="translate('button.terms_of_service')"
					@click="goToUrl(termsOfServiceUrl)"
				/>
				<AppTextButton
					class="service-buttons__btn"
					:type="StatusTypes.DEFAULT"
					:text="translate('button.privacy_policy')"
					@click="goToUrl(privacyPolicyUrl)"
				/>
			</div>
			<AppTextButton
				class="service-buttons__btn"
				:type="StatusTypes.DEFAULT"
				:text="translate('button.game_rules')"
				@click="openRulesPopup"
			/>
			<div
				v-if="userStore.showPrivacyOptionsSettings"
				class="service-buttons__link"
				@click="communicator.showPrivacyOptionsFormUmp()"
			>
				{{ translate("button.privacy_settings") }}
			</div>
		</div>
		<HorizontalDecorDivider />
		<AppTextButton
			class="delete-account__btn"
			:type="StatusTypes.DANGER"
			:text="translate('ui.delete_account')"
			@click="deleteAccount"
		/>
		<div class="delete-account__notice">
			{{ translate("ui.delete_account_warning") }}
		</div>
		<ToastMessage
			class="settings-message"
			:class="{
				'settings-message--copied':
					toastMessage === 'copied_to_clipboard',
			}"
			:show-message="!!toastMessage"
			:message="toastMessage ?? ''"
		/>
	</div>
</template>

<script lang="ts" setup>
import AppIconButton from "@/components/AppIconButton.vue";
import AppTextButton from "@/components/AppTextButton.vue";
import HorizontalDecorDivider from "@/components/SVG/HorizontalDecorDivider.vue";
import ToastMessage from "@/components/ToastMessage.vue";
import SoundService from "@/game/SoundService";
import useSocketService from "@/hooks/useSocketService";
import Communicator, {
	SocialMediaType,
	type LinkTypes,
} from "@/libs/AppCommunicatior";
import {
	PopupDictionary,
	PopupType,
	usePopupStore,
} from "@/store/usePopupStore";
import useRuntimeStore from "@/store/useRuntimeStore";
import useUserStore from "@/store/useUserStore";
import { IconTypes, SkinTypes, StatusTypes } from "@/types/ui";
import { translate } from "@/utils/translate";
import { privacyPolicyUrl, termsOfServiceUrl } from "@/utils/url";
import { storeToRefs } from "pinia";
import { computed, markRaw, ref } from "vue";

const socketService = markRaw(useSocketService());
const soundService = markRaw(SoundService.getInstance());
const communicator = markRaw(Communicator.getInstance());

const emitGenericClick = (): void => {
	soundService.playGenerickClick();
};

const popupStore = usePopupStore();

const userStore = useUserStore();
const { linkedSocialMediaType } = storeToRefs(userStore);

// TODO napojit spravne data
const editingInProgress = ref<boolean>(false);
const toastMessage = ref<string | null>(null);

enum AccountItemType {
	USERNAME = "username",
	EMAIL = "email",
	PLAYER_ID = "player_id",
}

interface AccountItem {
	type: AccountItemType;
	value: string;
	hideItem?: boolean;
	text: string;
}
const hideEmail = computed((): boolean => {
	if (!userStore.userEmail || linkedSocialMediaType.value.length === 0)
		return true;

	const regex = /^powerplaymanager\+[A-Za-z0-9_-]{8,10}@gmail\.com$/;
	return regex.test(userStore.userEmail ?? "");
});

const accountItems = computed((): Array<AccountItem> => {
	return [
		{
			type: AccountItemType.USERNAME,
			value: userStore.userName,
			text:
				editingInProgress.value || !userStore.userName
					? "new_username"
					: "username",
		},
		{
			type: AccountItemType.EMAIL,
			value: userStore.userEmail,
			hideItem: hideEmail.value,
			text: "email",
		},
		{
			type: AccountItemType.PLAYER_ID,
			value: userStore.userId,
			text: "player_id",
		},
	];
});

const btnIcon = (type: AccountItemType): IconTypes => {
	emitGenericClick();
	if (type === AccountItemType.USERNAME) {
		if (editingInProgress.value || !userStore.userName) {
			return IconTypes.SAVE;
		} else {
			return IconTypes.EDIT_LIGHT;
		}
	}
	return IconTypes.COPY;
};

const btnAction = async (type: AccountItemType): Promise<void> => {
	emitGenericClick();
	if (type === AccountItemType.USERNAME) {
		showChangeNamePopup();
	}
	if (type === AccountItemType.PLAYER_ID) {
		copy();
	}
};
const showChangeNamePopup = (): void => {
	emitGenericClick();
	usePopupStore().showPopup({
		id: PopupDictionary.CHANGE_NAME,
		type: PopupType.CHANGE_NAME,
		dropOnInterrupt: true,
		heading: translate("ui.username"),
		height: 13.75,
		isPaddingless: true,
	});
};

const linkAccount = (
	type: LinkTypes | undefined,
	socialMediaType: SocialMediaType,
): void => {
	emitGenericClick();
	useRuntimeStore().setLinkingAccountSocialMediaType(socialMediaType);
	communicator.linkAccount(type, socialMediaType);
};

const getSocialMediaTypeText = (socialMediaType: SocialMediaType): string => {
	switch (socialMediaType) {
		case "google":
			return translate("button.google.non_translatable");
		case "apple":
			return translate("button.apple_id.non_translatable");
		default:
			return "";
	}
};
const copy = async (): Promise<void> => {
	emitGenericClick();
	await navigator.clipboard.writeText(userStore.userId ?? "");
	handleMessage("copied_to_clipboard");
};

const openRulesPopup = (): void => {
	popupStore.showPopup({
		id: PopupDictionary.RULES,
		type: PopupType.RULES,
		heading: translate("button.game_rules"),
		isPaddingless: true,
		skin: SkinTypes.DARK,
		maxHeight: 40,
		width: 22.5,
	});
};

const handleMessage = (message: string): void => {
	toastMessage.value = message;
	setTimeout((): void => {
		toastMessage.value = null;
	}, 1_000);
};
const goToUrl = (url: string): void => {
	window.open(url, "_blank");
};

const deleteAccount = (): void => {
	popupStore.showPopup({
		id: PopupDictionary.DELETE_ACCOUNT,
		type: PopupType.DELETE,
		heading: translate("ui.delete_account"),
		body: translate("ui.delete_account_warning"),
		skin: SkinTypes.LIGHT,
		actions: [
			{
				label: translate("button.delete"),
				buttonType: StatusTypes.DANGER,
				callback: async (): Promise<void> => {
					await socketService.deleteAccount();
				},
			},
			{
				label: translate("button.back"),
				buttonType: StatusTypes.PRIMARY,
				callback: (): void => {
					popupStore.hidePopup();
				},
			},
		],
		height: 13,
		isCentered: true,
		isPaddingless: true,
	});
	emitGenericClick();
	//
	// Tu uz nic nebude, lebo metoda vyssie posle signal Flutteru na reset.
};
</script>

<style lang="scss" scoped>
.account-settings {
	width: 100%;
	@include flex-col-start;
	position: relative;
	gap: 1rem;
	margin-bottom: 0.3rem;
	padding: 1.25rem;

	&__row {
		display: flex;
		justify-content: space-between;
		width: 100%;
		font-size: 0.813rem;

		&-left {
			display: flex;
			align-items: center;
			color: #fff3dd;
			line-height: 1rem;
		}
		&-right {
			display: flex;
			justify-content: flex-end;
			align-items: center;
			&-text {
				max-width: 14rem;
				overflow: hidden;
				color: #fff3dd;
				font-weight: bold;
				text-overflow: ellipsis;
				text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
				white-space: nowrap;
			}
		}
		&-button {
			margin-left: 0.5rem;
		}
		&.username {
			font-size: 0.9rem;
			.account-settings__row-left {
				text-transform: uppercase;
			}
			.account-settings__row-right-text {
				max-width: 10rem;
			}
		}
		&.email {
			.account-settings__row-right-text {
				font-weight: normal;
			}
		}
	}
	.link-account {
		margin-top: 0.5rem;
		width: 100%;
		@include flex-col-center;
		&__title {
			margin-bottom: 0.4rem;
			color: #fff3dd;
			font-size: 0.813rem;
			text-align: center;
			text-transform: uppercase;
		}
		&__btn {
			width: 11rem;
			height: 2.25rem;
		}
		&--linked {
			display: flex;
			align-items: center;
			gap: 1rem;
			color: #19b345;
			font-weight: bold;
			font-size: 1rem;
			&--text {
				@include flex-row-center;
				gap: 0.25rem;
			}
			.icon-check {
				width: 0.875rem;
				height: 0.875rem;
			}
		}
		&--row {
			@include flex-row-center;
			gap: 1rem;
			width: 100%;
		}
	}
	.service-buttons {
		width: 100%;
		@include flex-col-center;
		gap: 1rem;

		&__row {
			width: 100%;
		}
		& > div {
			display: flex;
			gap: 1rem;
		}
		&__btn {
			width: 11rem;
			height: 2.25rem;
			line-height: 1.2rem;
		}
		&__link {
			cursor: pointer;
			color: #f2dcb5;
			text-decoration: underline;
		}
	}
	.delete-account {
		&__btn {
			width: 11rem;
			height: 2.25rem;
		}
		&__notice {
			color: #ff3d3d;
			font-size: 0.688rem;
			text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
		}
	}
	input {
		width: 12.688rem;
		height: 1.75rem;
	}
	.settings-message {
		position: absolute;
		top: 0;
		right: 0.6rem;
		&--copied {
			top: 4rem;
		}
	}

	:deep(.horizontal-decor-divider) {
		width: 100%;
	}
}
</style>
